import { useEffect, useRef, useState } from 'react';
import { Bluetooth, BluetoothConnected, X } from 'lucide-react';
import type { Button as IcButton, IntercomConfig } from '@livepuls/types';
import { usePanelStore } from '../store/panel-store.js';
import { deriveLabel } from '../lib/labels.js';
import { unlockAudio } from '../lib/livekit.js';
import {
  hwConnect,
  hwDisconnect,
  hwSupport,
  onHwEvent,
  HW_VOLUME_EVENT,
  type HwSource,
  type HwStatus,
} from '../lib/hardware-ptt.js';

const VOLUME_STEP = 5;

/** Kanalnamen kurz ansagen (Sprachausgabe, Deutsch). No-op ohne Support. */
function speak(text: string): void {
  try {
    const synth = window.speechSynthesis;
    if (!synth || !text) return;
    synth.cancel(); // laufende Ansage abbrechen → immer nur der aktuelle Kanal
    const u = new SpeechSynthesisUtterance(text);
    u.lang = 'de-DE';
    u.rate = 1.05;
    synth.speak(u);
  } catch {
    /* Sprachausgabe optional */
  }
}

interface Props {
  panelId: string;
  buttons: IcButton[]; // pressbare Buttons dieses Panels, in Layout-Reihenfolge
  config: IntercomConfig;
}

interface Settings {
  source: HwSource;
  url: string;
  targetButtonId: string | null;
}

function loadSettings(panelId: string, fallbackButtonId: string | null): Settings {
  try {
    const raw = localStorage.getItem(`livepuls.hwptt.${panelId}`);
    if (raw) {
      const s = JSON.parse(raw) as Partial<Settings>;
      return {
        source: s.source === 'ws' ? 'ws' : 'ble',
        url: typeof s.url === 'string' ? s.url : 'ws://localhost:8770',
        targetButtonId: s.targetButtonId ?? fallbackButtonId,
      };
    }
  } catch {
    /* ignore */
  }
  return { source: 'ble', url: 'ws://localhost:8770', targetButtonId: fallbackButtonId };
}

/**
 * Optionale Hardware-PTT-Steuerung (BM008 & Co). Rendert nur ein kleines
 * Bluetooth-Icon im Header; die eigentliche Verbindung startet erst auf
 * ausdrücklichen Wunsch. Ohne Verbindung passiert nichts.
 */
export function HardwarePtt({ panelId, buttons, config }: Props): JSX.Element {
  const support = hwSupport();
  const setPressed = usePanelStore((s) => s.setPressed);
  const setHwTarget = usePanelStore((s) => s.setHwTarget);

  const firstId = buttons[0]?.id ?? null;
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState<Settings>(() => loadSettings(panelId, firstId));
  const [status, setStatus] = useState<HwStatus>('idle');
  const [detail, setDetail] = useState<string>('');
  // Diagnose: wie viele Hardware-Events kamen an + welches zuletzt.
  const [eventCount, setEventCount] = useState(0);
  const [lastEvent, setLastEvent] = useState('–');

  // Persistieren.
  useEffect(() => {
    try {
      localStorage.setItem(`livepuls.hwptt.${panelId}`, JSON.stringify(settings));
    } catch {
      /* ignore */
    }
  }, [panelId, settings]);

  // Ziel-Taste IMMER gültig halten: fehlt sie oder existiert sie im aktuellen
  // Layout nicht (mehr), automatisch auf die erste Taste setzen. Sonst liefe
  // PTT ins Leere und es gäbe nie einen grünen Rahmen.
  const buttonIdsKey = buttons.map((b) => b.id).join(',');
  useEffect(() => {
    const ids = buttonIdsKey ? buttonIdsKey.split(',') : [];
    setSettings((s) => {
      if (s.targetButtonId && ids.includes(s.targetButtonId)) return s;
      const first = ids[0] ?? null;
      return s.targetButtonId === first ? s : { ...s, targetButtonId: first };
    });
  }, [buttonIdsKey]);

  // Aktuelle Werte für den EINMALIG registrierten Event-Handler.
  const liveRef = useRef({ settings, buttons, config });
  liveRef.current = { settings, buttons, config };
  // Ist die Hardware-PTT gerade „gedrückt"? (für sauberes Loslassen bei Abriss)
  const pttDownFor = useRef<string | null>(null);

  const releaseIfHeld = (): void => {
    const id = pttDownFor.current;
    if (id) {
      pttDownFor.current = null;
      setPressed(id, false);
    }
  };

  // Event-Strom der Hardware auf die Panel-Aktionen abbilden. EINMALIG gehängt.
  useEffect(() => {
    const off = onHwEvent((e) => {
      const { settings: st, buttons: btns, config: cfg } = liveRef.current;
      const buttonIds = btns.map((b) => b.id);
      // Diagnose: jedes eingehende Event festhalten.
      setEventCount((n) => n + 1);
      setLastEvent(
        e.kind === 'status'
          ? `status:${e.state}`
          : e.kind === 'ptt'
          ? `ptt ${e.down ? 'down' : 'up'}`
          : `taste ${e.name}`,
      );
      if (e.kind === 'status') {
        setStatus(e.state);
        setDetail(e.detail ?? '');
        if (e.state !== 'connected') releaseIfHeld();
        return;
      }
      if (e.kind === 'ptt') {
        if (e.down) {
          const id = st.targetButtonId;
          if (!id || !buttonIds.includes(id)) return; // Ziel existiert (noch)?
          pttDownFor.current = id;
          setPressed(id, true);
        } else {
          // Beim Loslassen IMMER die tatsächlich gedrückte Taste lösen –
          // auch wenn die Zieltaste zwischenzeitlich gewechselt wurde.
          releaseIfHeld();
        }
        return;
      }
      // Einzel-Tasten.
      if (e.name === 'CH') {
        // Zieltaste zyklisch weiterschalten (evtl. gehaltene PTT vorher lösen).
        releaseIfHeld();
        if (buttonIds.length === 0) return;
        const cur = st.targetButtonId;
        const idx = cur ? buttonIds.indexOf(cur) : -1;
        const nextBtn = btns[(idx + 1) % btns.length]!;
        setSettings((s) => ({ ...s, targetButtonId: nextBtn.id }));
        // Kanalnamen kurz ansagen.
        speak(deriveLabel(nextBtn, cfg));
      } else if (e.name === 'LAUT' || e.name === 'LEISE') {
        const id = st.targetButtonId;
        if (!id || !buttonIds.includes(id)) return;
        const delta = e.name === 'LAUT' ? VOLUME_STEP : -VOLUME_STEP;
        window.dispatchEvent(
          new CustomEvent(HW_VOLUME_EVENT, { detail: { buttonId: id, delta } }),
        );
      }
      // SOS: bewusst ohne Wirkung (kein versehentliches Call-to-All).
    });
    return () => {
      off();
      releaseIfHeld();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Grünen Rahmen setzen: nur WÄHREND einer Verbindung die Zieltaste markieren.
  const connectedNow = status === 'connected';
  useEffect(() => {
    setHwTarget(connectedNow ? settings.targetButtonId : null);
    return () => setHwTarget(null);
  }, [connectedNow, settings.targetButtonId, setHwTarget]);

  const connected = status === 'connected';
  const connecting = status === 'searching';

  async function toggleConnect(): Promise<void> {
    if (connected || connecting) {
      await hwDisconnect();
      return;
    }
    // WICHTIG: Dieser Klick ist eine Nutzergeste → hier Audio-Wiedergabe und
    // Sprachausgabe freischalten, damit später PTT (Sprechen) und die
    // Kanal-Ansagen aus dem Hardware-Callback funktionieren.
    unlockAudio();
    const cur = buttons.find((b) => b.id === settings.targetButtonId) ?? null;
    if (cur) speak(deriveLabel(cur, config));
    try {
      await hwConnect(settings.source, { url: settings.url });
    } catch {
      /* Status-Event trägt den Fehler bereits. */
    }
  }

  const targetBtn = buttons.find((b) => b.id === settings.targetButtonId) ?? null;
  const targetLabel = targetBtn ? deriveLabel(targetBtn, config) : '—';

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Hardware-PTT (Funktaster)"
        title={connected ? `Funktaster verbunden → ${targetLabel}` : 'Funktaster (optional)'}
        className={[
          'shrink-0 flex items-center justify-center rounded-full border w-8 h-8 transition-colors',
          connected
            ? 'border-accent/60 bg-accent/10 text-accent'
            : 'border-border bg-surface text-text-mute hover:text-text',
        ].join(' ')}
      >
        {connected ? <BluetoothConnected size={16} /> : <Bluetooth size={16} />}
      </button>

      {open ? (
        <div className="absolute right-0 z-50 mt-2 w-72 rounded-card border border-border bg-surface p-3 shadow-xl text-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="font-semibold text-text">Funktaster (optional)</span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-text-mute hover:text-text"
              aria-label="Schließen"
            >
              <X size={15} />
            </button>
          </div>

          {/* Status */}
          <div className="mb-3 flex items-center gap-2 text-xs">
            <span
              className={[
                'inline-block w-2 h-2 rounded-full',
                connected ? 'bg-ok' : connecting ? 'bg-warn' : 'bg-text-mute/50',
              ].join(' ')}
            />
            <span className="text-text-dim">
              {connected
                ? `verbunden${detail ? ` · ${detail}` : ''}`
                : connecting
                ? 'suche…'
                : status === 'error'
                ? `Fehler${detail ? `: ${detail}` : ''}`
                : 'nicht verbunden'}
            </span>
          </div>

          {/* Diagnose: kommen überhaupt Events an? */}
          <div className="mb-3 rounded bg-bg/60 border border-border px-2 py-1.5 font-mono text-xxs text-text-mute">
            Events: <span className="text-accent">{eventCount}</span> · zuletzt:{' '}
            <span className="text-text">{lastEvent}</span>
            {' · '}Web-BT: {support.ble ? 'ja' : 'nein'}
          </div>

          {/* Verbindungsart */}
          <label className="block text-xxs uppercase tracking-wider text-text-mute mb-1">
            Verbindung
          </label>
          <select
            className="w-full mb-2 px-2 py-1.5 rounded-btn bg-bg border border-border text-text disabled:opacity-50"
            value={settings.source}
            disabled={connected || connecting}
            onChange={(e) => setSettings((s) => ({ ...s, source: e.target.value as HwSource }))}
          >
            <option value="ble" disabled={!support.ble}>
              Direkt (Web Bluetooth){support.ble ? '' : ' – hier nicht verfügbar'}
            </option>
            <option value="ws" disabled={!support.ws}>
              Bridge (WebSocket)
            </option>
          </select>

          {settings.source === 'ws' ? (
            <input
              className="w-full mb-2 px-2 py-1.5 rounded-btn bg-bg border border-border text-text font-mono text-xs disabled:opacity-50"
              value={settings.url}
              disabled={connected || connecting}
              onChange={(e) => setSettings((s) => ({ ...s, url: e.target.value }))}
              placeholder="ws://localhost:8770"
            />
          ) : null}

          {/* Zieltaste */}
          <label className="block text-xxs uppercase tracking-wider text-text-mute mb-1 mt-1">
            PTT steuert Taste
          </label>
          <select
            className="w-full mb-3 px-2 py-1.5 rounded-btn bg-bg border border-border text-text"
            value={settings.targetButtonId ?? ''}
            onChange={(e) =>
              setSettings((s) => ({ ...s, targetButtonId: e.target.value || null }))
            }
          >
            {buttons.length === 0 ? <option value="">— keine Tasten —</option> : null}
            {buttons.map((b) => (
              <option key={b.id} value={b.id}>
                {deriveLabel(b, config)}
              </option>
            ))}
          </select>

          <button
            type="button"
            onClick={() => void toggleConnect()}
            disabled={settings.source === 'ble' && !support.ble}
            className={[
              'w-full px-3 py-2 rounded-btn font-semibold transition-colors disabled:opacity-40',
              connected || connecting
                ? 'bg-err/15 text-err hover:bg-err/25'
                : 'bg-accent text-black hover:bg-accent/90',
            ].join(' ')}
          >
            {connected || connecting ? 'Trennen' : 'Verbinden'}
          </button>

          <p className="mt-2 text-xxs leading-snug text-text-mute">
            PTT-Taste = sprechen auf der gewählten Taste. <strong>CH</strong> wechselt die
            Taste, <strong>Laut/Leise</strong> regelt deren Lautstärke. Ohne Verbindung
            arbeitet das Panel ganz normal weiter.
          </p>
        </div>
      ) : null}
    </div>
  );
}
